import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Pencil, Trash2, Waves, BookOpen } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useSetHelpKey } from "@/context/HelpContext";

function pick(post, lang, field) {
  if (lang === "en") {
    return post[`${field}_en`] || post[`${field}_de`];
  }
  return post[`${field}_de`] || post[`${field}_en`];
}

function CoverBlock({ hue = 197, size = "md" }) {
  const style = {
    background: `linear-gradient(140deg, hsl(${hue} 70% 62%) 0%, hsl(${(hue + 40) % 360} 72% 42%) 100%)`,
  };
  return (
    <div
      className={`relative overflow-hidden rounded-t-md ${size === "sm" ? "h-28" : "h-40"}`}
      style={style}
    >
      <Waves className="absolute -bottom-3 -right-3 h-24 w-24 text-white/25" />
    </div>
  );
}

function BodyText({ text }) {
  if (!text) return null;
  return (
    <div className="space-y-4">
      {text.split(/\n\n+/).map((para, i) => (
        <p key={i} className="text-[15px] leading-relaxed text-foreground">
          {para.split("\n").map((line, j, arr) => (
            <React.Fragment key={j}>
              {line}
              {j < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}

export function BlogList() {
  useSetHelpKey("blog.list");
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog/posts")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-caps text-primary">
            <BookOpen className="mr-1 inline h-3.5 w-3.5" /> {t("blog_kicker")}
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t("blog_title")}
          </h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            {t("blog_intro")}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">…</div>
      ) : posts.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="blog-empty">
          {t("blog_empty")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              className="group card-hover border-border overflow-hidden rounded-md border bg-card"
              data-testid={`blog-card-${p.slug}`}
            >
              <CoverBlock hue={p.cover_hue} size="sm" />
              <div className="space-y-3 p-5">
                {p.category && (
                  <div className="label-caps text-primary">{p.category}</div>
                )}
                <h3 className="text-lg font-semibold leading-snug transition-colors group-hover:text-primary">
                  {pick(p, lang, "title")}
                </h3>
                <p className="line-clamp-3 text-sm text-muted-foreground">
                  {pick(p, lang, "excerpt")}
                </p>
                <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
                  <span>{p.author_name || "Water Planner"}</span>
                  {p.created_at && <span>{formatDate(p.created_at)}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlogPost() {
  useSetHelpKey("blog.post");
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl py-10">
        <h1 className="text-2xl font-semibold">{t("blog_not_found")}</h1>
        <Link
          to="/blog"
          className="mt-4 inline-flex items-center gap-2 text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("back")}
        </Link>
      </div>
    );
  }

  if (!post) return <div className="text-sm text-muted-foreground">…</div>;

  return (
    <article className="mx-auto max-w-3xl" data-testid="blog-post-article">
      <Link
        to="/blog"
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
        data-testid="blog-back-link"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("blog_all_posts")}
      </Link>
      <Card className="overflow-hidden border-border">
        <CoverBlock hue={post.cover_hue} />
        <CardContent className="space-y-6 p-8">
          {post.category && (
            <div className="label-caps text-primary">{post.category}</div>
          )}
          <div>
            <h1
              className="text-3xl font-bold leading-tight sm:text-4xl"
              data-testid="blog-post-title"
            >
              {pick(post, lang, "title")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {post.author_name || "Water Planner"} ·{" "}
              {post.created_at ? formatDate(post.created_at) : ""}
            </p>
          </div>
          <BodyText text={pick(post, lang, "body")} />
        </CardContent>
      </Card>
    </article>
  );
}

export function BlogAdmin() {
  useSetHelpKey("blog.admin");
  const { t } = useI18n();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // post|null|"__new__"
  const isMuni = user?.role === "municipality";

  const load = () => api.get("/blog/posts?include_drafts=1").then((r) => setPosts(r.data));

  useEffect(() => {
    load();
  }, []);

  const remove = async (p) => {
    if (!window.confirm(`${t("delete")} "${p.title_de}"?`)) return;
    await api.delete(`/blog/posts/${p.id}`);
    toast.success(t("post_deleted"));
    await load();
  };

  if (!isMuni) return null;

  return (
    <Card className="border-border" data-testid="blog-admin-card">
      <CardContent className="space-y-4 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">{t("blog_admin_title")}</h3>
            <p className="text-sm text-muted-foreground">
              {t("blog_admin_hint")}
            </p>
          </div>
          <Button
            onClick={() => setEditing("__new__")}
            data-testid="blog-new-btn"
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {t("new_post")}
          </Button>
        </div>
        {posts.length === 0 ? (
          <div className="text-sm text-muted-foreground">{t("blog_empty")}</div>
        ) : (
          <ul className="divide-y divide-border">
            {posts.map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center gap-3 py-3"
                data-testid={`blog-admin-row-${p.slug}`}
              >
                <div className="flex-1">
                  <div className="text-sm font-semibold">{p.title_de}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.slug} ·{" "}
                    {p.published ? t("published") : t("draft")} ·{" "}
                    {p.updated_at ? formatDate(p.updated_at) : ""}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(p)}
                  data-testid={`blog-edit-${p.slug}`}
                  className="gap-1"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {t("edit")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(p)}
                  data-testid={`blog-delete-${p.slug}`}
                  className="gap-1 text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("delete")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {editing && (
        <BlogEditor
          initial={editing === "__new__" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </Card>
  );
}

function BlogEditor({ initial, onClose, onSaved }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title_de: initial?.title_de || "",
    title_en: initial?.title_en || "",
    excerpt_de: initial?.excerpt_de || "",
    excerpt_en: initial?.excerpt_en || "",
    body_de: initial?.body_de || "",
    body_en: initial?.body_en || "",
    category: initial?.category || "",
    cover_hue: initial?.cover_hue ?? 197,
    published: initial?.published ?? true,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial) {
        await api.patch(`/blog/posts/${initial.id}`, form);
      } else {
        await api.post("/blog/posts", form);
      }
      toast.success(t("post_saved"));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="blog-editor-dialog">
        <DialogHeader>
          <DialogTitle>
            {initial ? t("edit_post") : t("new_post")}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t("title")} (DE)</Label>
              <Input
                required
                value={form.title_de}
                onChange={(e) => set("title_de", e.target.value)}
                data-testid="blog-title-de-input"
              />
            </div>
            <div>
              <Label>{t("title")} (EN)</Label>
              <Input
                value={form.title_en}
                onChange={(e) => set("title_en", e.target.value)}
                data-testid="blog-title-en-input"
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t("category")}</Label>
              <Input
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                placeholder={t("blog_category_hint")}
              />
            </div>
            <div>
              <Label>{t("cover_hue")}</Label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="359"
                  value={form.cover_hue}
                  onChange={(e) => set("cover_hue", parseInt(e.target.value, 10))}
                  className="w-full accent-primary"
                  data-testid="blog-hue-input"
                />
                <div
                  className="h-8 w-12 rounded-md border border-border"
                  style={{
                    background: `linear-gradient(135deg, hsl(${form.cover_hue} 70% 62%), hsl(${(form.cover_hue + 40) % 360} 72% 42%))`,
                  }}
                />
              </div>
            </div>
          </div>
          <div>
            <Label>{t("excerpt")} (DE)</Label>
            <Textarea
              rows={2}
              value={form.excerpt_de}
              onChange={(e) => set("excerpt_de", e.target.value)}
              data-testid="blog-excerpt-de-input"
            />
          </div>
          <div>
            <Label>{t("body")} (DE)</Label>
            <Textarea
              rows={10}
              required
              value={form.body_de}
              onChange={(e) => set("body_de", e.target.value)}
              data-testid="blog-body-de-input"
            />
          </div>
          <details className="rounded-md border border-border p-3">
            <summary className="cursor-pointer text-sm">
              {t("blog_optional_en")}
            </summary>
            <div className="mt-3 space-y-3">
              <div>
                <Label>{t("excerpt")} (EN)</Label>
                <Textarea
                  rows={2}
                  value={form.excerpt_en}
                  onChange={(e) => set("excerpt_en", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("body")} (EN)</Label>
                <Textarea
                  rows={8}
                  value={form.body_en}
                  onChange={(e) => set("body_en", e.target.value)}
                />
              </div>
            </div>
          </details>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => set("published", e.target.checked)}
              className="h-4 w-4 accent-primary"
              data-testid="blog-published-input"
            />
            {t("published")}
          </label>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="blog-editor-cancel"
            >
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={busy} data-testid="blog-editor-save">
              {busy ? "…" : t("save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BlogTile() {
  const { t } = useI18n();
  return (
    <Link
      to="/blog"
      className="card-hover flex items-center gap-4 rounded-md border border-border bg-primary/[0.04] p-5 transition-colors hover:border-primary/60"
      data-testid="blog-dashboard-tile"
    >
      <div className="grid h-11 w-11 place-items-center rounded-md bg-primary/10 text-primary">
        <BookOpen className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <div className="label-caps text-primary">Water Planner Blog</div>
        <div className="mt-0.5 text-sm font-semibold">{t("blog_tile_title")}</div>
        <div className="text-xs text-muted-foreground">
          {t("blog_tile_sub")}
        </div>
      </div>
    </Link>
  );
}
